import { t } from "@travily/i18n";
import individualIllustration from "@ui/assets/illustrations/individual-animated.webp";
import { Wizard } from "@ui/components/Wizard";
import { WizardTextArea } from "@ui/components/WizardTextArea";
import type { ReactNode } from "react";

const COLOR = "purple" as const;

export interface IndividualBioStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage (also used by the Skip action). */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current value of the bio field. */
  value: string;
  /** Called with the new value on every field change. */
  onChange: (value: string) => void;
}

export function IndividualBioStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  onChange,
}: IndividualBioStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.SecondaryButton onPress={onPrimary} mobileLabel={t("common.actions.skip")}>
        {t("common.actions.skip")}
      </Wizard.SecondaryButton>
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="h-32 w-auto" src={individualIllustration} />}
        heading={t("web.individuals.creator.steps.bio.show.title")}
        helper={t("web.individuals.creator.steps.bio.show.subtitle")}
      >
        <div className="w-full max-w-[600px]">
          <WizardTextArea
            autoFocus
            color={COLOR}
            isLabelHidden
            label={t("web.accounts.setup.steps.bio.label")}
            onChange={onChange}
            placeholder={t("web.individuals.creator.steps.bio.show.placeholder")}
            rows={4}
            value={value}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
